import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ship, Wind, Camera, MapPin, Calendar, AlertTriangle, Anchor } from 'lucide-react';
import './GuidePage.css';

export default function IslandsGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🏝️ Islands - Skye, Orkney & Shetland</h1>
        
        <div className="info-box">
          <p>
            Schottlands Inseln sind das ultimative Radfahrer-Abenteuer: Dramatische Küsten, 
            einsame Straßen, Wildlife und prähistorische Stätten. Aber: <strong>Wind, Fähren 
            und Wetter</strong> erfordern gute Planung!
          </p>
        </div>

        <section>
          <h2>Übersicht: Die drei Inselgruppen</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <h3>🏔️ Isle of Skye</h3>
              <p><strong>Charakter:</strong> Dramatisch & bergig</p>
              <p><strong>Distanz:</strong> ~80km Hauptroute</p>
              <p><strong>Beste Zeit:</strong> Mai - September</p>
            </div>
            <div className="data-item">
              <h3>🏛️ Orkney Islands</h3>
              <p><strong>Charakter:</strong> Flach & historisch</p>
              <p><strong>Distanz:</strong> ~200km alle Inseln</p>
              <p><strong>Beste Zeit:</strong> Mai - August</p>
            </div>
            <div className="data-item">
              <h3>🐦 Shetland Islands</h3>
              <p><strong>Charakter:</strong> Rau & abgelegen</p>
              <p><strong>Distanz:</strong> ~250km Mainland</p>
              <p><strong>Beste Zeit:</strong> Juni - August</p>
            </div>
          </div>

          <h3>Schwierigkeitsgrad</h3>
          <ul>
            <li><strong>Skye:</strong> ⭐⭐⭐⭐ (Schwer - Berge & Wind)</li>
            <li><strong>Orkney:</strong> ⭐⭐ (Leicht - Flach, aber Wind!)</li>
            <li><strong>Shetland:</strong> ⭐⭐⭐ (Mittel - Hügel & extremer Wind)</li>
          </ul>
        </section>

        <section>
          <h2>1. Isle of Skye - Die Dramatische</h2>
          
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Skye ist KEIN Anfänger-Terrain!</strong> Steile Anstiege (bis 20%), 
            starker Wind und wechselhaftes Wetter. Aber: Die Landschaft ist atemberaubend!
          </div>

          <h3><Ship size={20} /> Anreise</h3>
          <ul>
            <li><strong>Skye Bridge (Kyle of Lochalsh):</strong> Keine Fähre nötig, direkt mit Rad</li>
            <li><strong>Fähre Mallaig - Armadale:</strong> 30 Min., mehrmals täglich (Calmac)</li>
            <li><strong>Fähre Glenelg - Kylerhea:</strong> 5 Min., klein & charmant (Turntable Ferry)</li>
          </ul>

          <h3>Top-Routen auf Skye</h3>
          
          <h4>1. Portree - The Storr (20km return)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Mittel</li>
              <li><strong>Höhenmeter:</strong> 400m</li>
              <li><strong>Highlights:</strong> Old Man of Storr, Panorama-Blick</li>
              <li><strong>Tipp:</strong> Früh starten (weniger Touristen)</li>
            </ul>
          </div>

          <h4>2. Sligachan - Elgol (25km one way)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Schwer</li>
              <li><strong>Höhenmeter:</strong> 600m</li>
              <li><strong>Highlights:</strong> Cuillin Mountains, Loch Coruisk View</li>
              <li><strong>Tipp:</strong> Single-track road, vorsichtig fahren</li>
            </ul>
          </div>

          <h4>3. Uig - Quiraing Loop (35km)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Schwer</li>
              <li><strong>Höhenmeter:</strong> 800m</li>
              <li><strong>Highlights:</strong> Quiraing-Pass (spektakulär!)</li>
              <li><strong>Tipp:</strong> 20% Steigung am Pass - E-Bike empfohlen</li>
            </ul>
          </div>

          <h4>4. Rundtour Skye (160km, 3-4 Tage)</h4>
          <ul>
            <li><strong>Tag 1:</strong> Armadale - Portree (60km, 900m HM)</li>
            <li><strong>Tag 2:</strong> Portree - Dunvegan (40km, 500m HM)</li>
            <li><strong>Tag 3:</strong> Dunvegan - Uig - Portree (60km, 1000m HM)</li>
          </ul>

          <h3>Sehenswürdigkeiten Skye</h3>
          <div className="checklist">
            <div className="check-item"><Camera /> <strong>Old Man of Storr:</strong> Ikonischer Felsturm (Wanderung 1h)</div>
            <div className="check-item"><Camera /> <strong>Fairy Pools:</strong> Kristallklare Pools (7km vom Rad)</div>
            <div className="check-item"><Camera /> <strong>Neist Point:</strong> Leuchtturm am westlichsten Punkt</div>
            <div className="check-item"><Camera /> <strong>Dunvegan Castle:</strong> Ältestes bewohntes Schloss</div>
            <div className="check-item"><Camera /> <strong>Quiraing:</strong> Dramatische Landslide-Landschaft</div>
          </div>

          <h3>Praktische Tipps Skye</h3>
          <div className="pro-tip">
            <h4>💡 Skye-Survival-Guide</h4>
            <ul>
              <li><strong>Unterkünfte vorbuchen!</strong> Skye ist sehr touristisch (Sommer = voll)</li>
              <li><strong>Single-Track Roads:</strong> Meist schmal, Passing Places nutzen</li>
              <li><strong>Lebensmittel:</strong> Nur in Portree, Broadford, Dunvegan gut einkaufen</li>
              <li><strong>Tankstellen:</strong> Wenige, immer vollgetankt (E-Bike: Ladegerät!)</li>
              <li><strong>Midges:</strong> Im Sommer extrem! Midge-Netz & Spray Pflicht</li>
              <li><strong>Wetter:</strong> Kann sich alle 15 Min. ändern - Regenkleidung bereit</li>
            </ul>
          </div>

          <h3>Beste Reisezeit Skye</h3>
          <ul>
            <li><strong>Mai - Juni:</strong> Weniger Touristen, längere Tage, Midges beginnen</li>
            <li><strong>Juli - August:</strong> Wärmster, aber vollste Zeit & Midges-Höhepunkt</li>
            <li><strong>September:</strong> Ruhiger, Herbstfarben, weniger Midges, mehr Regen</li>
            <li><strong>Oktober - April:</strong> Viele Unterkünfte geschlossen, stürmisch</li>
          </ul>
        </section>

        <section>
          <h2>2. Orkney Islands - Die Historische</h2>
          
          <div className="info-box">
            <p>
              Orkney ist flach (perfekt für entspanntes Radfahren!), aber <strong>der Wind ist 
              brutal</strong>. Dafür: 5000 Jahre Geschichte, Wikinger-Erbe und freundliche Menschen.
            </p>
          </div>

          <h3><Ship size={20} /> Anreise</h3>
          <ul>
            <li><strong>Fähre Scrabster - Stromness:</strong> 1.5h, mehrmals täglich (Calmac/Pentland Ferries)</li>
            <li><strong>Fähre Gills Bay - St Margaret's Hope:</strong> 1h, täglich</li>
            <li><strong>Fähre Aberdeen - Kirkwall:</strong> 6h Nachtfähre (alternativ)</li>
            <li><strong>Flug:</strong> Von Edinburgh/Glasgow nach Kirkwall (Rad als Gepäck möglich)</li>
          </ul>

          <h3>Orkney Mainland - Haupt-Routen</h3>
          
          <h4>1. Kirkwall - Stromness (25km, West Mainland Road)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Leicht</li>
              <li><strong>Höhenmeter:</strong> 150m</li>
              <li><strong>Highlights:</strong> Standing Stones of Stenness, Ring of Brodgar</li>
              <li><strong>Route:</strong> A965 (Hauptstraße, Verkehr!)</li>
            </ul>
          </div>

          <h4>2. Rundtour Orkney Mainland (120km, 2-3 Tage)</h4>
          <ul>
            <li><strong>Tag 1:</strong> Stromness - Birsay - Evie (50km) - Westküste</li>
            <li><strong>Tag 2:</strong> Evie - Kirkwall - St Margaret's Hope (70km) - Ostküste</li>
            <li><strong>Tag 3:</strong> Flexibel: Inter-Island Fähren (siehe unten)</li>
          </ul>

          <h4>3. Insel-Hopping (mit Fähren)</h4>
          <div className="pro-tip">
            <h4><Anchor size={20} /> Inter-Island Ferries</h4>
            <p><strong>Orkney hat 20 bewohnte Inseln!</strong> Mit Fähren erreichbar:</p>
            <ul>
              <li><strong>Hoy:</strong> Bergigste Insel, Old Man of Hoy (Fähre Stromness/Houton)</li>
              <li><strong>South Ronaldsay:</strong> Via Churchill Barriers (keine Fähre nötig!)</li>
              <li><strong>Rousay:</strong> "Egypt of the North" (viele Cairns), Fähre von Tingwall</li>
              <li><strong>Westray:</strong> Abgelegen & ruhig, Puffins!, Fähre von Kirkwall</li>
            </ul>
            <p><strong>Tipp:</strong> Fähren für Rad meist gratis oder sehr günstig!</p>
          </div>

          <h3>Must-See auf Orkney</h3>
          <div className="checklist">
            <div className="check-item"><MapPin /> <strong>Skara Brae:</strong> 5000 Jahre altes Dorf (UNESCO)</div>
            <div className="check-item"><MapPin /> <strong>Ring of Brodgar:</strong> Steinkreis älter als Stonehenge</div>
            <div className="check-item"><MapPin /> <strong>Maeshowe:</strong> Grabhügel mit Wikinger-Runen</div>
            <div className="check-item"><MapPin /> <strong>Italian Chapel:</strong> WWII Kapelle (South Ronaldsay)</div>
            <div className="check-item"><MapPin /> <strong>Brough of Birsay:</strong> Wikinger-Siedlung (Gezeiteninsel!)</div>
            <div className="check-item"><MapPin /> <strong>Old Man of Hoy:</strong> 137m Fels-Stack (nur von Hoy sichtbar)</div>
          </div>

          <h3>Praktische Tipps Orkney</h3>
          <ul>
            <li><strong>Wind, Wind, Wind:</strong> Durchschnittlich 30 km/h, oft mehr!</li>
            <li><strong>Bäume gibt's nicht:</strong> Kein Windschutz - Pausen planen</li>
            <li><strong>Flaches Terrain:</strong> Perfekt für Anfänger (trotz Wind)</li>
            <li><strong>Lebensmittel:</strong> Kirkwall & Stromness gut versorgt</li>
            <li><strong>Unterkünfte:</strong> B&Bs überall, wild campen erlaubt (Scottish Outdoor Access Code)</li>
            <li><strong>Internet:</strong> Gut (auch auf kleinen Inseln)</li>
            <li><strong>Fähren:</strong> VORHER buchen (besonders Sommer & Wochenenden)</li>
          </ul>

          <h3>Beste Reisezeit Orkney</h3>
          <ul>
            <li><strong>Mai - Juni:</strong> Längste Tage (22h Tageslicht!), Puffins auf Westray</li>
            <li><strong>Juli - August:</strong> Wärmster, aber auch windigste Zeit</li>
            <li><strong>September:</strong> Noch gutes Wetter, weniger Wind, weniger Touristen</li>
            <li><strong>Oktober - April:</strong> Sehr windig & stürmisch, viele Fähren ausgesetzt</li>
          </ul>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Orkney Wind-Warnung:</strong> Bei Sturmwarnung (>40mph/65km/h Wind) bleibe 
            im Hostel! Radfahren bei Sturm ist lebensgefährlich - du wirst umgeweht!
          </div>
        </section>

        <section>
          <h2>3. Shetland Islands - Die Abgelegene</h2>
          
          <div className="info-box">
            <p>
              Shetland ist Schottlands nördlichste Inselgruppe - näher an Bergen (Norwegen) als 
              Aberdeen! <strong>Extrem rau, windig und abgelegen</strong>, aber: Unglaubliche 
              Natur, Wildlife (Puffins, Otter, Seals) und herzliche Menschen.
            </p>
          </div>

          <h3><Ship size={20} /> Anreise</h3>
          <ul>
            <li><strong>Fähre Aberdeen - Lerwick:</strong> 12-14h Nachtfähre (NorthLink Ferries)</li>
            <li><strong>Fähre Kirkwall (Orkney) - Lerwick:</strong> 6h (kombinierbar mit Orkney-Tour)</li>
            <li><strong>Flug:</strong> Von Edinburgh/Glasgow/Aberdeen nach Sumburgh (tgl.)</li>
          </ul>

          <h3>Shetland Mainland - Top-Routen</h3>
          
          <h4>1. Lerwick - Sumburgh (40km, South Mainland)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Mittel</li>
              <li><strong>Höhenmeter:</strong> 400m</li>
              <li><strong>Highlights:</strong> Sumburgh Head (Puffins!), Jarlshof (Wikinger-Ruinen)</li>
              <li><strong>Tipp:</strong> Starker Südwind möglich - Rückweg anstrengend!</li>
            </ul>
          </div>

          <h4>2. Lerwick - Scalloway (10km, West)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Schwierigkeit:</strong> Leicht</li>
              <li><strong>Höhenmeter:</strong> 150m</li>
              <li><strong>Highlights:</strong> Scalloway Castle, alte Hauptstadt</li>
              <li><strong>Tipp:</strong> Kurze Etappe, gut für Akklimatisierung</li>
            </ul>
          </div>

          <h4>3. Rundtour Shetland Mainland (250km, 5-7 Tage)</h4>
          <ul>
            <li><strong>Tag 1:</strong> Lerwick - Sumburgh (40km)</li>
            <li><strong>Tag 2:</strong> Sumburgh - Bigton - Scalloway (50km)</li>
            <li><strong>Tag 3:</strong> Scalloway - Walls - Aith (40km)</li>
            <li><strong>Tag 4:</strong> Aith - Brae - Hillswick (50km)</li>
            <li><strong>Tag 5:</strong> Hillswick - Eshaness - Mossbank (60km)</li>
            <li><strong>Tag 6:</strong> Mossbank - Lerwick (40km)</li>
          </ul>

          <h4>4. Insel-Hopping Shetland</h4>
          <div className="pro-tip">
            <h4><Anchor size={20} /> Äußere Inseln</h4>
            <p><strong>Mit Fähren erreichbar:</strong></p>
            <ul>
              <li><strong>Yell & Unst:</strong> Nördlichste bewohnte UK-Inseln (Fähre Toft-Ulsta)</li>
              <li><strong>Whalsay:</strong> Fischerinsel (Fähre Laxo-Symbister)</li>
              <li><strong>Fair Isle:</strong> Zwischen Orkney & Shetland (Vogel-Paradies, schwer erreichbar)</li>
              <li><strong>Foula:</strong> Abgelegenste Insel (nur 30 Einwohner, Fähre unregelmäßig)</li>
            </ul>
            <p><strong>Tipp:</strong> Unst = "Most Northerly Point in Britain" - gute Bragging Rights!</p>
          </div>

          <h3>Must-See auf Shetland</h3>
          <div className="checklist">
            <div className="check-item"><Camera /> <strong>Sumburgh Head:</strong> Puffins (Mai-August), Leuchtturm</div>
            <div className="check-item"><Camera /> <strong>Jarlshof:</strong> 4000 Jahre Siedlungsgeschichte</div>
            <div className="check-item"><Camera /> <strong>Eshaness:</strong> Dramatische Klippen & Blowholes</div>
            <div className="check-item"><Camera /> <strong>St Ninian's Isle:</strong> Tombolo (Sandbank verbindet Insel)</div>
            <div className="check-item"><Camera /> <strong>Mousa Broch:</strong> 2000 Jahre alter Steinturm (Boots-Trip nötig)</div>
            <div className="check-item"><Camera /> <strong>Unst:</strong> Northernmost Point UK (Muckle Flugga)</div>
          </div>

          <h3>Wildlife auf Shetland</h3>
          <ul>
            <li><strong>Puffins:</strong> Sumburgh Head (Mai-August)</li>
            <li><strong>Otter:</strong> Häufig an Küsten (früh morgens/abends)</li>
            <li><strong>Seals:</strong> Überall an Stränden</li>
            <li><strong>Orca:</strong> Selten, aber möglich (Sommer)</li>
            <li><strong>Shetland Ponies:</strong> Überall! (Nicht füttern)</li>
            <li><strong>Seevögel:</strong> Möwen, Skuas, Terns (aggressive Skuas in Brutzeit!)</li>
          </ul>

          <h3>Praktische Tipps Shetland</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Shetland ist EXTREM!</strong> Wind, Regen, Kälte auch im Sommer möglich. 
            Das ist nichts für unvorbereitete Radfahrer!
          </div>

          <ul>
            <li><strong>Wind noch extremer als Orkney:</strong> 40+ km/h normal</li>
            <li><strong>Keine Bäume:</strong> Null Windschutz (außer in Lerwick)</li>
            <li><strong>Wetter wechselt schnell:</strong> 4 Jahreszeiten pro Stunde</li>
            <li><strong>Lebensmittel:</strong> Lerwick gut, sonst nur kleine Shops</li>
            <li><strong>Tankstellen/Laden:</strong> Selten im Norden - plane voraus!</li>
            <li><strong>Unterkünfte:</strong> Begrenzt - vorbuchen! Wild Camping erlaubt</li>
            <li><strong>Summertime:</strong> Im Juni nie dunkel (Simmer Dim)</li>
            <li><strong>Internet:</strong> Überraschend gut (Glasfaser!)</li>
            <li><strong>Distanzen täuschen:</strong> Wind macht 30km zu 60km-Gefühl</li>
          </ul>

          <h3>Beste Reisezeit Shetland</h3>
          <ul>
            <li><strong>Juni - Juli:</strong> Beste Zeit! Simmer Dim (weiße Nächte), Puffins, Up Helly Aa (Jan!)</li>
            <li><strong>August:</strong> Noch gut, aber Puffins ab Mitte Aug weg</li>
            <li><strong>Mai & September:</strong> Möglich, aber kälter & windiger</li>
            <li><strong>Oktober - April:</strong> Sehr stürmisch, viele Fähren ausgesetzt, Unterkünfte geschlossen</li>
          </ul>

          <div className="pro-tip">
            <h4>💡 Shetland-Überlebens-Tipps</h4>
            <ul>
              <li><strong>Kleinere Gänge einpacken:</strong> Wind macht alles schwerer</li>
              <li><strong>Wetterbericht TÄGLICH checken:</strong> Bei Sturm nicht fahren!</li>
              <li><strong>Notfall-Snacks immer dabei:</strong> Cafés sind rar</li>
              <li><strong>Warme Kleidung auch im Sommer:</strong> 15°C ist "warm"</li>
              <li><strong>Regenklamotten griffbereit:</strong> Regen kommt aus dem Nichts</li>
              <li><strong>Flexibler Reiseplan:</strong> Wind kann Etappen verdoppeln/halbieren</li>
              <li><strong>Locals fragen:</strong> Shetlander sind hilfsbereit & kennen Wind-Routen</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>4. Islands-Vergleich: Welche Insel für wen?</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <h3>🏔️ Skye für...</h3>
              <ul>
                <li>Bergfans (Cuillin!)</li>
                <li>Fotografen (dramatisch!)</li>
                <li>Erfahrene Radfahrer</li>
                <li>Whisky-Liebhaber (Talisker)</li>
              </ul>
            </div>
            <div className="data-item">
              <h3>🏛️ Orkney für...</h3>
              <ul>
                <li>Geschichts-Fans</li>
                <li>Anfänger (flach!)</li>
                <li>Wikinger-Nerds</li>
                <li>Insel-Hopping-Fans</li>
              </ul>
            </div>
            <div className="data-item">
              <h3>🐦 Shetland für...</h3>
              <ul>
                <li>Abenteurer</li>
                <li>Wildlife-Fans (Puffins!)</li>
                <li>Einsamkeits-Sucher</li>
                <li>Wind-Masochisten 😅</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2>5. Fähren & Transport</h2>
          
          <h3>Fähren buchen</h3>
          <ul>
            <li><strong>Calmac (Skye, Orkney):</strong> calmac.co.uk - mind. 1 Woche vorher</li>
            <li><strong>NorthLink (Shetland):</strong> northlinkferries.co.uk - mind. 2 Wochen vorher</li>
            <li><strong>Orkney Ferries (Inter-Island):</strong> orkneyferries.co.uk - oft spontan OK</li>
          </ul>

          <h3>Rad auf Fähren</h3>
          <div className="info-box">
            <ul>
              <li><strong>Rad zählt als Fahrzeug:</strong> Separat buchen (günstiger als Auto!)</li>
              <li><strong>E-Bike Akku:</strong> Manche Fähren verlangen Akku-Entnahme</li>
              <li><strong>Früh da sein:</strong> Räder werden zuerst verladen</li>
              <li><strong>Spanngurte:</strong> Fähre stellt oft (frag Personal!)</li>
            </ul>
          </div>

          <h3>Kombinierte Island-Hopping-Tour</h3>
          <div className="pro-tip">
            <h4>🗺️ 14-Tage Ultimate Islands Tour</h4>
            <ul>
              <li><strong>Tag 1-4:</strong> Skye (Rundtour)</li>
              <li><strong>Tag 5:</strong> Fahrt nach Scrabster (160km oder Zug)</li>
              <li><strong>Tag 6-9:</strong> Orkney (Mainland + 1-2 Inseln)</li>
              <li><strong>Tag 10:</strong> Fähre Kirkwall - Lerwick (6h)</li>
              <li><strong>Tag 11-14:</strong> Shetland (Mainland Rundtour)</li>
            </ul>
            <p><strong>Gesamt:</strong> ~600km, 6000+ Höhenmeter, 3 Fähren, unvergesslich!</p>
          </div>
        </section>

        <section>
          <h2>6. Ausrüstung für Island-Touren</h2>
          
          <h3>Zusätzlich zur Standard-Ausrüstung:</h3>
          <div className="checklist">
            <div className="check-item">✅ <strong>Wasserdichte Packtaschen</strong> (Regen ist garantiert!)</div>
            <div className="check-item">✅ <strong>Warme Schichten</strong> (Fleece/Daunenweste auch im Sommer)</div>
            <div className="check-item">✅ <strong>Windbreaker</strong> (essentiell!)</div>
            <div className="check-item">✅ <strong>Midge-Netz & Spray</strong> (Skye!)</div>
            <div className="check-item">✅ <strong>Ersatzakku/Powerbank</strong> (wenig Lademöglichkeiten)</div>
            <div className="check-item">✅ <strong>Offline-Karten</strong> (GPS Signal teilweise schlecht)</div>
            <div className="check-item">✅ <strong>Notfall-Snacks</strong> (Cafés/Shops selten)</div>
            <div className="check-item">✅ <strong>Fernglas</strong> (Wildlife!)</div>
          </div>

          <h3>Nicht vergessen:</h3>
          <ul>
            <li><strong>Fähren-Tickets ausdrucken</strong> (kein Internet auf See!)</li>
            <li><strong>Bargeld</strong> (kleine B&Bs nehmen oft nur Cash)</li>
            <li><strong>EHIC/Reiseversicherung</strong> (Krankenhäuser weit weg)</li>
          </ul>
        </section>

        <div className="warning-box">
          <AlertTriangle size={24} />
          <div>
            <strong>Wichtigster Tipp für alle Islands:</strong><br />
            Sei flexibel! Wind & Wetter diktieren deinen Plan, nicht dein Kalender. 
            Einen Sturm-Tag im Pub zu verbringen ist besser als vom Rad geweht zu werden!
          </div>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstungs-Guide</Link> - Packliste für Islands</li>
            <li><Link to="/wissen/fahrtechnik">🚴 Fahrtechnik</Link> - Wind & Anstiege meistern</li>
            <li><Link to="/wissen/sicherheit">🦺 Sicherheit</Link> - Notfallplanung</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Downloads</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Islands-Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
